import {
  BetaTag,
  Button,
  Heading,
  Label,
  Modal,
  MultiSegmentControl,
  Option,
  Switch,
  toast,
} from "@indigo-components";
import { FormattedTextAreaBlock } from "@legacy-components/blocks/FormattedTextArea";
import { FC, useCallback, useEffect, useState } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { FontSize, Theme, useFontSize, useTheme } from "~/hooks/useTheme";
import { useUserSettings } from "~/hooks/useUserSettings";
import { AuthUserGroup } from "~/types/auth";
import { useOwnSecuritySettings } from "./useOwnSecuritySettings";

interface SettingsOption<T> {
  label: string; // Professional label (for DOCTOR)
  altLabel: string; // Fun label (for ADMIN/MEDICAL_ASSISTANT)
  value: T; // Actual value (FontSize | Theme)
  hidden: boolean; // Hide experimental options
}

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userGroup: AuthUserGroup;
}

// Font size options with dual labels
const fontSizeOptions: SettingsOption<FontSize>[] = [
  { label: "Smaller", altLabel: "Eric", value: "smaller", hidden: false },
  { label: "Default", altLabel: "Default", value: "default", hidden: false },
  { label: "Larger", altLabel: "Helen", value: "larger", hidden: false },
];

// Theme options with dual labels
const themeOptions: SettingsOption<Theme>[] = [
  { label: "Light", altLabel: "Cloud", value: "light", hidden: false },
  { label: "Warm", altLabel: "Latte", value: "latte", hidden: false },
  {
    label: "Banana",
    altLabel: "Banana",
    value: "overripe-banana",
    hidden: true, // Hidden from Doctor roles (experimental)
  },
  { label: "Dark", altLabel: "Licorice", value: "dark", hidden: false },
];

// Ops checklist default. Unlike the two above this one is not a rendering
// preference, so it persists on the user record instead of localStorage.
// MultiSegmentControl is string-keyed, so the boolean the field stores is
// mapped at this boundary rather than threaded through the control.
type OpsChecklistDefault = "expanded" | "collapsed";

const opsChecklistOptions: Option<OpsChecklistDefault>[] = [
  { label: "Expanded", value: "expanded" },
  { label: "Collapsed", value: "collapsed" },
];

// Groups the Ops checklist sidebar auto-opens for — mirrors MA_SCOPED_GROUPS in
// AppointmentDetailsPage, which is what decides whether the auto-open runs at
// all. Doctors and historians never see the sidebar, so the setting is hidden
// from them rather than offered as a no-op.
const OPS_CHECKLIST_GROUPS: ReadonlyArray<AuthUserGroup> = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
];

/**
 * Convert SettingsOption array to Option array based on user group
 * - DOCTOR users see professional labels and hidden options are filtered OUT
 * - ADMIN/MEDICAL_ASSISTANT users see fun labels and all options (including experimental/hidden)
 */
const getDisplayOptions = <T,>(
  options: SettingsOption<T>[],
  userGroup: AuthUserGroup,
): Option<T>[] => {
  const isDoctor = userGroup === AuthUserGroup.DOCTOR;

  return options
    .filter((opt) => {
      // For doctors: exclude hidden options
      // For ADMIN/MA: include all options (even hidden ones)
      return !isDoctor || !opt.hidden;
    })
    .map((opt) => ({
      label: isDoctor ? opt.label : opt.altLabel,
      value: opt.value,
    }));
};

const UserSettingsModal: FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
  userGroup,
}) => {
  // Theme and font size are localStorage-only, so they apply immediately.
  const [currentTheme, setCurrentTheme] = useTheme();
  const [currentFontSize, setCurrentFontSize] = useFontSize();
  const { settings, updateSettings, isSaving } = useUserSettings();
  const { authSession } = useAuthSession();
  const ownCognitoSub = authSession?.tokens?.accessToken.payload.sub;
  const twoFactor = useOwnSecuritySettings(ownCognitoSub, isOpen);

  // Server-backed, so held locally and written on Save. Null (never set)
  // reads as collapsed.
  const savedOpsChecklist: OpsChecklistDefault =
    settings.opsChecklistDefaultExpanded ? "expanded" : "collapsed";
  const [localOpsChecklist, setLocalOpsChecklist] =
    useState<OpsChecklistDefault>(savedOpsChecklist);

  // Reset on open; also picks up the saved value if it loads after opening.
  useEffect(() => {
    if (isOpen) setLocalOpsChecklist(savedOpsChecklist);
  }, [isOpen, savedOpsChecklist]);

  // Get display options based on user group
  const displayFontSizeOptions = getDisplayOptions(fontSizeOptions, userGroup);
  const displayThemeOptions = getDisplayOptions(themeOptions, userGroup);
  const showOpsChecklistSetting = OPS_CHECKLIST_GROUPS.includes(userGroup);

  const saveOpsChecklist = useCallback(async (): Promise<boolean> => {
    if (!showOpsChecklistSetting || localOpsChecklist === savedOpsChecklist) {
      return true;
    }
    const saved = await updateSettings({
      opsChecklistDefaultExpanded: localOpsChecklist === "expanded",
    });
    // Closing on a failed write would leave the user believing the default
    // changed, and they'd only find out on the next case.
    if (!saved) {
      toast({
        message: "Could not save your Ops checklist default. Please retry.",
        variant: "error",
      });
    }
    return saved;
  }, [
    showOpsChecklistSetting,
    localOpsChecklist,
    savedOpsChecklist,
    updateSettings,
  ]);

  // Only server-backed edits need a Save; X discards them.
  const hasChanges =
    (showOpsChecklistSetting && localOpsChecklist !== savedOpsChecklist) ||
    twoFactor.isDirty;

  const { save: saveTwoFactor } = twoFactor;
  const handleConfirm = useCallback(async () => {
    // Each save toasts its own failure; stay open on any so Save can be
    // retried.
    const results = await Promise.all([saveOpsChecklist(), saveTwoFactor()]);
    if (results.every(Boolean)) onClose();
  }, [saveOpsChecklist, saveTwoFactor, onClose]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={520}
      className="flex flex-col justify-between p-6"
      title={<Heading level={1}>User Settings</Heading>}
    >
      <div className="flex flex-col gap-4">
        <section className="flex flex-col gap-4">
          <Heading level={2}>Preferences</Heading>
          {/* Font Size Control */}
          <div className="flex justify-between items-center ">
            <Label size="lg" weight="medium">
              Font size
            </Label>
            <MultiSegmentControl
              options={displayFontSizeOptions}
              value={currentFontSize}
              onChange={(value: FontSize) => setCurrentFontSize(value)}
              variant="filled"
            />
          </div>

          {/* Theme Color Control */}
          <div className="flex justify-between items-center">
            {/* Beta until every theme has had a full QA pass. */}
            <div className="flex items-center gap-2">
              <Label size="lg" weight="medium">
                Theme color
              </Label>
              <BetaTag />
            </div>
            <MultiSegmentControl
              options={displayThemeOptions}
              value={currentTheme}
              onChange={(value: Theme) => setCurrentTheme(value)}
              variant="filled"
            />
          </div>

          {/* Ops Checklist Default */}
          {showOpsChecklistSetting && (
            <div className="flex justify-between items-center">
              <Label size="lg" weight="medium">
                Ops checklist default
              </Label>
              <MultiSegmentControl
                options={opsChecklistOptions}
                value={localOpsChecklist}
                onChange={(value: OpsChecklistDefault) =>
                  setLocalOpsChecklist(value)
                }
                variant="filled"
              />
            </div>
          )}
        </section>

        {ownCognitoSub && (
          <section className="flex flex-col gap-4 border-t border-indigo-border-subtle pt-4">
            <Heading level={2}>2FA Settings</Heading>
            {twoFactor.loadError ? (
              <span className="text-rose-600">
                Error: {twoFactor.loadError}
              </span>
            ) : (
              <>
                <div className="flex justify-between items-center gap-4">
                  <Label size="lg" weight="medium">
                    Phone number
                  </Label>
                  <div className="w-48">
                    <FormattedTextAreaBlock
                      type="phoneNumber"
                      value={twoFactor.phoneDigits}
                      onChange={twoFactor.setPhoneDigits}
                      disabled={twoFactor.isLoading}
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <div className="flex flex-col">
                    <Label size="lg" weight="medium">
                      SMS two-factor auth
                    </Label>
                    {!twoFactor.smsMfaEnabled && !twoFactor.canEnableMfa && (
                      <span className="text-xs text-indigo-text-tertiary">
                        Set a phone number first to enable SMS 2FA.
                      </span>
                    )}
                  </div>
                  <Switch
                    checked={twoFactor.smsMfaEnabled}
                    onChange={twoFactor.setSmsMfaEnabled}
                    disabled={
                      twoFactor.isLoading ||
                      (!twoFactor.smsMfaEnabled && !twoFactor.canEnableMfa)
                    }
                  />
                </div>
              </>
            )}
          </section>
        )}

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 mt-2">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleConfirm}
            loading={isSaving || twoFactor.isSaving}
          >
            {hasChanges ? "Save" : "Done"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UserSettingsModal;
